// src/pages/AddProduct.jsx
import React, { useState, useEffect, useMemo } from 'react';
import Button from '../customButtons/Button';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

// --- Enhanced Data Definitions ---

const productCategories = {
    input: ['Fertiliser', 'Seedlings', 'Pesticide', 'Herbicide', 'Organics', 'seeds', 'Fungicides', 'Soil Amendments'],
    produce: ['Fruits', 'Vegetables', 'Cereals', 'Legumes']
};

const unitsOfMeasurement = [
    'kg', 'g', 'ton', 'lbs', 'oz',
    'liter', 'ml', 'gallon',
    'bushels', 'bags', 'packets', 'sacks', 'bales',
    'units', 'pieces', 'crates', 'dozens', 'bunches'
];

// Enhanced unit information with conversion factors and categories
const unitInformation = {
    'kg': { type: 'mass', baseUnit: 'g', factor: 1000, name: 'Kilograms' },
    'g': { type: 'mass', baseUnit: 'g', factor: 1, name: 'Grams' },
    'ton': { type: 'mass', baseUnit: 'kg', factor: 1000, name: 'Metric Tons' },
    'lbs': { type: 'mass', baseUnit: 'g', factor: 453.592, name: 'Pounds' },
    'oz': { type: 'mass', baseUnit: 'g', factor: 28.3495, name: 'Ounces' },
    'liter': { type: 'volume', baseUnit: 'ml', factor: 1000, name: 'Liters' },
    'ml': { type: 'volume', baseUnit: 'ml', factor: 1, name: 'Milliliters' },
    'gallon': { type: 'volume', baseUnit: 'ml', factor: 3785.41, name: 'Gallons' },
    'bushels': { type: 'volume', baseUnit: 'liter', factor: 35.2391, name: 'Bushels' },
    'bags': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Bags' },
    'packets': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Packets' },
    'sacks': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Sacks' },
    'bales': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Bales' },
    'units': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Units' },
    'pieces': { type: 'countable', baseUnit: 'pieces', factor: 1, name: 'Pieces' },
    'crates': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Crates' },
    'dozens': { type: 'countable', baseUnit: 'pieces', factor: 12, name: 'Dozens' },
    'bunches': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Bunches' }
};

// Helper components remain the same...
const InputField = ({ id, label, error, hint, ...props }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {hint && (
                <span className="ml-2 text-xs text-gray-400 font-normal italic">
                    ({hint})
                </span>
            )}
        </label>
        <input
            id={id}
            name={id}
            className={`block w-full border ${error ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4
                       focus:outline-none focus:ring-2 focus:ring-custom-teal focus:border-custom-teal sm:text-base`}
            {...props}
        />
        {error && <p className="mt-1 text-sm text-red-600 font-medium">{error}</p>}
    </div>
);

const SelectField = ({ id, label, error, options, ...props }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
            {label}
        </label>
        <select
            id={id}
            name={id}
            className={`mt-1 block w-full pl-4 pr-10 py-2.5 text-base border-gray-300 rounded-lg shadow-sm
                       focus:outline-none focus:ring-2 focus:ring-custom-teal focus:border-custom-teal sm:text-base cursor-pointer ${error ? 'border-red-400' : ''}`}
            {...props}
        >
            {options.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label || option.value}
                </option>
            ))}
        </select>
        {error && <p className="mt-1 text-sm text-red-600 font-medium">{error}</p>}
    </div>
);

// --- Information Tooltip Component ---
const InfoTooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
        <div className="relative inline-block">
            <div
                className="inline-flex items-center justify-center w-4 h-4 ml-1 bg-gray-200 rounded-full cursor-help text-xs text-gray-600"
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
            >
                ?
            </div>
            {isVisible && (
                <div className="absolute z-10 w-64 p-3 mt-2 text-sm text-white bg-black border border-gray-200 rounded-lg shadow-lg -left-32">
                    {content}
                    {children}
                </div>
            )}
        </div>
    );
};

// --- Main Component with Fixed Calculations ---
const AddProduct = ({ onClose }) => {
    const { token, isAuthenticated, user, loading: authLoading } = useAuth();

    const [formData, setFormData] = useState({
        name: '',
        type: 'input',
        category: productCategories['input'][0],
        description: '',
        price: '',
        discountedPrice: '',
        quantity: '',
        unit: 'kg',
        baseUnit: 'g',
        minOrderQuantity: 1,
        expiry: '',
        image: '',
        tags: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiMessage, setApiMessage] = useState('');

    // --- FIXED CALCULATION FUNCTIONS ---

    /**
     * Convert quantity from one unit to another using proper conversion factors
     */
    const convertToBaseUnit = (quantity, fromUnit, toBaseUnit) => {
        if (fromUnit === toBaseUnit) return quantity;

        const fromInfo = unitInformation[fromUnit];
        const toInfo = unitInformation[toBaseUnit];

        if (!fromInfo || !toInfo) {
            console.warn(`Unknown unit conversion: ${fromUnit} to ${toBaseUnit}`);
            return quantity; // Fallback to 1:1
        }

        // If units are of different types, we cannot convert meaningfully
        if (fromInfo.type !== toInfo.type) {
            console.warn(`Cannot convert between different unit types: ${fromInfo.type} to ${toInfo.type}`);
            return quantity; // Fallback to 1:1
        }

        // Convert through base units if necessary
        if (fromUnit !== fromInfo.baseUnit) {
            // First convert to the fromUnit's base unit
            const inFromBase = quantity * fromInfo.factor;
            // Then convert from fromUnit's base unit to toBaseUnit's base unit
            if (fromInfo.baseUnit !== toInfo.baseUnit) {
                // This is where we'd need cross-base unit conversions
                // For now, we'll handle common cases
                if (fromInfo.baseUnit === 'g' && toInfo.baseUnit === 'kg') {
                    return inFromBase / 1000;
                } else if (fromInfo.baseUnit === 'kg' && toInfo.baseUnit === 'g') {
                    return inFromBase * 1000;
                } else if (fromInfo.baseUnit === 'ml' && toInfo.baseUnit === 'liter') {
                    return inFromBase / 1000;
                } else if (fromInfo.baseUnit === 'liter' && toInfo.baseUnit === 'ml') {
                    return inFromBase * 1000;
                }
            }
            return inFromBase; // If base units match after conversion
        }

        // Direct conversion using factors
        return quantity * fromInfo.factor;
    };

    /**
     * Calculate price per base unit correctly
     */
    const calculatePricePerBaseUnit = (totalPrice, totalQuantity, unit, baseUnit) => {
        if (totalQuantity <= 0 || totalPrice <= 0) return 0;

        const totalQuantityInBaseUnits = convertToBaseUnit(totalQuantity, unit, baseUnit);

        if (totalQuantityInBaseUnits <= 0) return 0;

        return totalPrice / totalQuantityInBaseUnits;
    };

    // Get compatible base units based on selected unit
    const currentBaseUnitOptions = useMemo(() => {
        const currentUnitInfo = unitInformation[formData.unit];
        if (!currentUnitInfo) return ['units', 'pieces', 'kg', 'g', 'liter', 'ml'];

        // Return units of the same type
        return Object.entries(unitInformation)
            .filter(([unit, info]) => info.type === currentUnitInfo.type)
            .map(([unit]) => unit);
    }, [formData.unit]);

    // Update category and baseUnit when product type or unit changes
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            category: productCategories[prev.type][0],
            baseUnit: currentBaseUnitOptions.includes(prev.baseUnit)
                ? prev.baseUnit
                : currentBaseUnitOptions[0] || 'units'
        }));
    }, [formData.type, currentBaseUnitOptions]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        const parsedPrice = parseFloat(formData.price);
        const parsedDiscountedPrice = parseFloat(formData.discountedPrice);
        const parsedQuantity = parseFloat(formData.quantity);
        const parsedMinOrderQuantity = parseFloat(formData.minOrderQuantity);

        if (!formData.name.trim()) newErrors.name = 'Product name is required';
        if (!formData.type) newErrors.type = 'Product type is required';
        if (!formData.category) newErrors.category = 'Category is required';

        if (isNaN(parsedPrice) || parsedPrice <= 0) newErrors.price = 'Valid total price is required';
        if (isNaN(parsedDiscountedPrice) || parsedDiscountedPrice < 0) {
            newErrors.discountedPrice = 'Valid total discounted price is required (use 0 if no discount)';
        } else if (parsedDiscountedPrice > parsedPrice) {
            newErrors.discountedPrice = 'Discounted price cannot be higher than original price';
        }
        if (isNaN(parsedQuantity) || parsedQuantity <= 0) newErrors.quantity = 'Valid total quantity is required';
        if (!formData.unit.trim()) newErrors.unit = 'Unit of measurement is required';
        if (!formData.baseUnit.trim()) newErrors.baseUnit = 'Base unit is required';

        // Validate unit compatibility
        const unitInfo = unitInformation[formData.unit];
        const baseUnitInfo = unitInformation[formData.baseUnit];
        if (unitInfo && baseUnitInfo && unitInfo.type !== baseUnitInfo.type) {
            newErrors.baseUnit = `Cannot convert ${unitInfo.name} to ${baseUnitInfo.name} - different measurement types`;
        }

        if (isNaN(parsedMinOrderQuantity) || parsedMinOrderQuantity <= 0) {
            newErrors.minOrderQuantity = 'Minimum order quantity must be a positive number';
        }
        if (!formData.expiry) newErrors.expiry = 'Expiry date is required';
        else if (new Date(formData.expiry) < new Date(new Date().toDateString())) {
            newErrors.expiry = 'Expiry date cannot be in the past';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            setApiMessage({ type: 'error', text: 'Please correct the errors in the form.' });
            return;
        }

        if (!isAuthenticated || !token || !user || !user.id) {
            setApiMessage({ type: 'error', text: 'Authentication error: User not logged in or ID missing.' });
            return;
        }

        setIsSubmitting(true);
        setApiMessage('');

        const qty = parseFloat(formData.quantity || 0);
        const priceVal = parseFloat(formData.price || 0);
        const discountedPriceVal = parseFloat(formData.discountedPrice || 0);
        const minOrderQtyVal = parseFloat(formData.minOrderQuantity || 0);

        // FIXED: Use proper calculation functions
        const pricePerBaseUnit = calculatePricePerBaseUnit(priceVal, qty, formData.unit, formData.baseUnit);
        const discountedPricePerBaseUnit = calculatePricePerBaseUnit(discountedPriceVal, qty, formData.unit, formData.baseUnit);

        const productData = {
            ...formData,
            price: priceVal,
            discountedPrice: discountedPriceVal,
            quantity: qty,
            minOrderQuantity: minOrderQtyVal,
            pricePerBaseUnit: pricePerBaseUnit,
            discountedPricePerBaseUnit: discountedPricePerBaseUnit,
            tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== ''),
            user: user.id
        };

        try {
            const response = await fetch(`${API_URL}/api/products`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(productData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add product');
            }

            const result = await response.json();
            setApiMessage({ type: 'success', text: `Product "${result.name}" added successfully! Redirecting...` });
            setFormData({
                name: '', type: 'input', category: productCategories['input'][0],
                description: '', price: '', discountedPrice: '', quantity: '', unit: 'kg', baseUnit: 'g',
                minOrderQuantity: 1,
                expiry: '', image: '', tags: ''
            });
            setErrors({});
            setTimeout(() => onClose(), 2000);
        } catch (err) {
            console.error('Add product failed:', err);
            setApiMessage({ type: 'error', text: err.message || 'An unexpected error occurred.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentCategoryOptions = productCategories[formData.type] || [];

    // FIXED: Price calculations using proper functions
    const pricePerUnit = useMemo(() => {
        const qty = parseFloat(formData.quantity);
        const price = parseFloat(formData.price);
        if (qty > 0 && price >= 0) {
            return (price / qty).toFixed(2);
        }
        return 'N/A';
    }, [formData.price, formData.quantity]);

    const pricePerBaseUnitDisplay = useMemo(() => {
        const qty = parseFloat(formData.quantity || 0);
        const priceVal = parseFloat(formData.price || 0);

        if (qty <= 0 || priceVal <= 0) return 'N/A';

        const calculated = calculatePricePerBaseUnit(priceVal, qty, formData.unit, formData.baseUnit);
        return calculated > 0.01 ? calculated.toFixed(4) : calculated.toFixed(6);
    }, [formData.price, formData.quantity, formData.unit, formData.baseUnit]);

    // Calculate discount percentage
    const discountPercentage = useMemo(() => {
        const price = parseFloat(formData.price);
        const discounted = parseFloat(formData.discountedPrice);
        if (price > 0 && discounted > 0 && discounted < price) {
            return ((price - discounted) / price * 100).toFixed(1);
        }
        return 0;
    }, [formData.price, formData.discountedPrice]);

    // Loading and authentication screens remain the same...
    if (authLoading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center font-inter text-custom-teal text-xl">
                <svg className="animate-spin h-8 w-8 mr-3 text-custom-teal" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading authentication...
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="bg-white py-10 px-6 shadow-2xl rounded-2xl sm:px-12 border border-gray-50 max-h-[90vh] overflow-y-auto">
                <h2 className="mt-2 text-center text-3xl font-extrabold text-red-600 mb-6">Access Denied</h2>
                <p className="text-center text-gray-700 mb-8">
                    You must be logged in to add new products. Please log in to continue.
                </p>
                <div className="flex justify-center">
                    <Button variant="primary" onClick={onClose}>Close</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 md:p-10 shadow-2xl rounded-2xl border border-gray-100 max-w-2xl w-full mx-auto animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
                <h2 className="text-[16px] md:text-md font-extrabold text-custom-teal">
                    Add New Product
                </h2>
            </div>

            <p className="mb-8 text-[14px] text-gray-600 border-b pb-4">
                Enter all required details for your product, including pricing and inventory specifications.
            </p>

            {apiMessage.text && (
                <div className={`mb-6 text-center font-medium p-3 rounded-lg border
                  ${apiMessage.type === 'success' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                    {apiMessage.text}
                </div>
            )}

            <form className="space-y-8" onSubmit={handleSubmit}>

                {/* 1. Product Details Section */}
                <div className="space-y-6">
                    <h3 className="text-md font-bold text-gray-800 border-b pb-2">Product Info</h3>

                    <InputField
                        id="name"
                        label="Product Name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g., High-Yield Maize Seed, Organic Tomatoes"
                        error={errors.name}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <SelectField
                                id="type"
                                label="Product Type"
                                value={formData.type}
                                onChange={handleChange}
                                options={[{ value: 'input', label: 'Farm Input' }, { value: 'produce', label: 'Farm Produce' }]}
                                error={errors.type}
                            />
                            {/* <InfoTooltip content="Farm Inputs: Seeds, fertilizers, tools. Farm Produce: Harvested crops for sale." /> */}
                        </div>

                        <div>
                            <SelectField
                                id="category"
                                label="Category"
                                value={formData.category}
                                onChange={handleChange}
                                options={currentCategoryOptions.map(cat => ({ value: cat, label: cat }))}
                                error={errors.category}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description (Optional)
                            <InfoTooltip content="Include key features, usage instructions, or special qualities that make your product stand out." />
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            placeholder="Brief description of the product, its uses, benefits..."
                            className={`block w-full border border-gray-300 rounded-lg shadow-sm py-2.5 px-4
                                       focus:outline-none focus:ring-2 focus:ring-custom-teal focus:border-custom-teal sm:text-base md:text-[12px]`}
                        ></textarea>
                    </div>
                </div>

                <div className="space-y-6">
                    <h3 className="text-md font-bold text-gray-800 border-b pb-2">Inventory & Pricing</h3>

                    {/* Quantity & Units Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <InputField
                                id="quantity"
                                label={
                                    <div className="flex items-center gap-1">
                                        <span>Total Stock Quantity</span>
                                        <InfoTooltip content="Total amount you have available for sale in your selected unit." />
                                    </div>
                                }
                                type="number"
                                value={formData.quantity}
                                onChange={handleChange}
                                placeholder="50"
                                min="0.01"
                                step="0.01"
                                error={errors.quantity}
                            />
                        </div>

                        <div>
                            <SelectField
                                id="unit"
                                label={
                                    <div>
                                        <span>Unit of Total Stock</span>
                                        <InfoTooltip content="The measurement unit for your total inventory quantity." />
                                    </div>
                                }
                                value={formData.unit}
                                onChange={handleChange}
                                options={unitsOfMeasurement.map(unit => ({
                                    value: unit,
                                    label: `${unit} (${unitInformation[unit]?.name || unit})`
                                }))}
                                error={errors.unit}
                            />
                        </div>
                    </div>

                    {/* Base Unit & Min Order Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <SelectField
                                id="baseUnit"
                                label={
                                    <div>
                                        <span>Smallest Sellable Unit</span>
                                        <InfoTooltip content="Customers will see prices per this unit. Choose the smallest practical unit you want to sell in." />
                                    </div>
                                }
                                value={formData.baseUnit}
                                onChange={handleChange}
                                options={currentBaseUnitOptions.map(unit => ({
                                    value: unit,
                                    label: `${unit} (${unitInformation[unit]?.name || unit})`
                                }))}
                                error={errors.baseUnit}
                            />
                            <p className="mt-1 text-xs text-gray-500 flex items-center">
                                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                This is the base unit for all per-unit prices.

                            </p>
                        </div>

                        <div>
                            <InputField
                                id="minOrderQuantity"
                                label={<div>
                                    <span>{`Minimum Order Quantity (${formData.baseUnit})`}</span>
                                    <InfoTooltip content="The smallest quantity a customer can purchase. Helps manage small orders." />
                                </div>}
                                type="number"
                                value={formData.minOrderQuantity}
                                onChange={handleChange}
                                placeholder="1"
                                min="1"
                                step="0.01"
                                error={errors.minOrderQuantity}
                            />

                        </div>
                    </div>

                    {/* Price Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <InputField
                                id="price"
                                label={
                                    <div>
                                        <span>
                                            Total Price (Kshs.)
                                        </span>
                                        <InfoTooltip content="The total price for your entire stock quantity shown above." />
                                    </div>
                                }
                                type="number"
                                value={formData.price}
                                onChange={handleChange}
                                placeholder="1000.00"
                                min="0.01"
                                step="0.01"
                                error={errors.price}
                            />

                        </div>

                        <div>
                            <InputField
                                id="discountedPrice"
                                label={
                                    <div>
                                        <span>Discounted Total Price (Kshs.)</span>
                                        <InfoTooltip content="Set a lower price for promotions. Use 0 if no discount. Must be lower than original price." />
                                    </div>
                                }
                                type="number"
                                value={formData.discountedPrice}
                                onChange={handleChange}
                                placeholder="800.00"
                                min="0"
                                step="0.01"
                                error={errors.discountedPrice}
                            />
                            {discountPercentage > 0 && (
                                <p className="mt-1 text-xs text-green-600 font-medium">
                                    {discountPercentage}% discount applied
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Enhanced Calculated Prices Display */}
                    <div className="mt-6 border-t pt-4">
                        <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                            Price Preview
                            <InfoTooltip content="These prices are calculated automatically based on your entries above. Customers will see the per-unit prices." />
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <label className="block text-xs font-semibold text-blue-700 mb-1">
                                    Price per {formData.unit}
                                </label>
                                <p className="text-lg font-bold text-blue-900">
                                    Kshs. {pricePerUnit} / {formData.unit}
                                </p>
                                <p className="text-xs text-blue-600 mt-1">
                                    Based on your total quantity and price
                                </p>
                            </div>

                            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <label className="block text-xs font-semibold text-purple-700 mb-1">
                                    Price per {formData.baseUnit}
                                </label>
                                <p className="text-lg font-bold text-purple-900">
                                    Kshs. {pricePerBaseUnitDisplay} / {formData.baseUnit}
                                </p>
                                <p className="text-xs text-purple-600 mt-1">
                                    Calculated using proper unit conversions
                                </p>
                            </div>
                        </div>

                        {/* Conversion Info */}
                        <div className="mt-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                            <strong>Conversion Note:</strong> {formData.quantity} {formData.unit} = {
                                convertToBaseUnit(parseFloat(formData.quantity || 1), formData.unit, formData.baseUnit).toFixed(4)
                            } {formData.baseUnit}
                        </div>
                    </div>
                </div>

                {/* 3. Optional/Other Details Section */}
                <div className="space-y-6">
                    <h3 className="text-md font-bold text-gray-800 border-b pb-2">Other Details</h3>

                    <div>
                        <InputField
                            id="expiry"
                            label={
                                <div>
                                    <span>Expiry Date</span>
                                    <InfoTooltip content="For perishable goods or products with shelf life. Leave empty for non-perishable items." />
                                </div>
                            }
                            type="date"
                            value={formData.expiry}
                            onChange={handleChange}
                            error={errors.expiry}
                        />
                    </div>

                    <div>
                        <InputField
                            id="image"
                            label={
                                <div>
                                    <span>Image Url</span>
                                    <InfoTooltip content="A direct link to the product photo. Use a clear, high-quality image. Recommended size: 800x600 pixels. You can use image hosting services like Imgur." />
                                </div>
                            }
                            type="url"
                            value={formData.image}
                            onChange={handleChange}
                            placeholder="https://example.com/product-image.jpg"
                        />
                    </div>

                    <div>
                        <InputField
                            id="product-tags"
                            label={
                                <div>
                                    <span>Product Tags</span>
                                    <InfoTooltip content="Comma-separated Keywords/Values that help customers find your product. Use relevant terms like 'organic', 'premium', 'local', etc." />
                                </div>
                            }
                            type="text"
                            value={formData.tags}
                            onChange={handleChange}
                            placeholder="e.g., organic, fast-growing, local"
                        />
                    </div>
                </div>

                {/* Submit/Cancel Buttons */}
                <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        variant="primary"
                        className='bg-teal-600 hover:bg-teal-700'
                    >
                        {isSubmitting ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Adding...
                            </span>
                        ) : 'Add Product'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default AddProduct;